import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
   Container,
   Box,
   Paper,
   Typography,
   Button,
   CircularProgress,
   Alert,
   Avatar,
   Chip,
   Card,
   CardContent,
   Divider,
   useMediaQuery,
   useTheme,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   IconButton,
   Snackbar,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   PersonAdd as PersonAddIcon,
   Home as HomeIcon,
   Group as GroupIcon,
   BusinessCenter as WorkIcon,
   Favorite as LoveIcon,
   Person as PersonIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
   RelationshipType,
   relationshipLabels,
   relationshipCategories,
} from '../types/Relationship';
import { getAuthHeaders } from '../utils/auth';
import axios from 'axios';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';

interface NetworkInvitation {
   id: string;
   fromUserId: string;
   fromUserName: string;
   fromUserEmail: string;
   fromUserPhoto?: string;
   toUserEmail: string;
   relationshipType?: RelationshipType;
   relationshipLabel?: string;
   message?: string;
   status: string;
   createdAt: string;
}

const AcceptNetworkInvite: React.FC = () => {
   const { requestId } = useParams<{ requestId: string }>();
   const [searchParams] = useSearchParams();
   const navigate = useNavigate();
   const { currentUser, isLoading: userLoading } = useUser();
   const { refreshInvitationStatus } = useSubscription();

   // Get token and other params from URL for token-based invitations
   const token = searchParams.get('token');
   const invitationId = searchParams.get('invitationId');
   const userEmail = searchParams.get('user_email');

   // Get responsive breakpoints
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [invitation, setInvitation] = useState<NetworkInvitation | null>(null);
   const [loading, setLoading] = useState(true);
   const [processing, setProcessing] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);

   // Registration modal state
   const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [name, setName] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>(
      'success'
   );
   const [pendingAction, setPendingAction] = useState<'accept' | 'decline' | null>(null);

   // Get relationship category icon
   const getRelationshipIcon = (relationshipType?: RelationshipType) => {
      if (!relationshipType) return <PersonIcon />;

      for (const [category, types] of Object.entries(relationshipCategories)) {
         if (types.includes(relationshipType)) {
            switch (category) {
               case 'Family':
                  return <HomeIcon />;
               case 'Friends':
                  return <GroupIcon />;
               case 'Work':
                  return <WorkIcon />;
               case 'Romantic':
                  return <LoveIcon />;
               default:
                  return <PersonIcon />;
            }
         }
      }

      return <PersonIcon />;
   };

   // No authentication check - users can view invitation without being logged in

   // Fetch invitation details
   useEffect(() => {
      const fetchInvitation = async () => {
         // Handle token-based invitations
         if (token && invitationId) {
            try {
               // Decode JWT token to get invitation data
               const base64Payload = token.split('.')[1];
               const payload = JSON.parse(atob(base64Payload));

               setInvitation({
                  id: payload.invitationId || invitationId,
                  fromUserId: payload.fromUserId,
                  fromUserName: payload.fromUserName,
                  fromUserEmail: payload.fromUserEmail,
                  toUserEmail: payload.toUserEmail,
                  relationshipType: payload.relationshipType,
                  relationshipLabel: payload.relationshipLabel,
                  message: payload.message,
                  status: 'pending',
                  createdAt: new Date().toISOString(),
               });
               setLoading(false);
               return;
            } catch (err) {
               console.error('Error decoding token:', err);
               setError('Invalid invitation token');
               setLoading(false);
               return;
            }
         }

         // Handle legacy path parameter approach
         if (!requestId) {
            setError('Invalid invitation link');
            setLoading(false);
            return;
         }

         try {
            const response = await axios.get(
               `${process.env.REACT_APP_API_URL}/api/network/invitation/${requestId}`
            );

            if (response.data.success) {
               setInvitation(response.data.invitation);
            } else {
               throw new Error(response.data.error || 'Invitation not found');
            }
         } catch (err: any) {
            console.error('Error fetching invitation:', err);
            setError(
               err.response?.data?.error ||
                  err.message ||
                  'Failed to load invitation. The link may be invalid or expired.'
            );
         } finally {
            setLoading(false);
         }
      };

      fetchInvitation();
   }, [requestId, token, invitationId]);

   // Handle accepting the invitation
   const handleAccept = async () => {
      if (!invitation) return;

      setPendingAction('accept');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('accept', invitation.toUserEmail);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('accept');
   };

   // Handle declining the invitation
   const handleDecline = async () => {
      if (!invitation) return;

      setPendingAction('decline');

      // If user is logged in, proceed directly
      if (currentUser?.uid) {
         await processInvitationAction('decline', invitation.toUserEmail);
         return;
      }

      // Check if user exists by email
      await checkUserAndProceed('decline');
   };

   const checkUserAndProceed = async (action: 'accept' | 'decline') => {
      if (!invitation) return;

      setProcessing(true);
      try {
         // Check if user exists
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email: invitation.toUserEmail }),
            }
         );

         const result = await response.json();

         if (result.exists) {
            // User exists, directly accept/decline using their email (no login required)
            await processInvitationAction(action, invitation.toUserEmail);
         } else {
            // User doesn't exist, show registration modal
            setEmail(invitation.toUserEmail);
            setRegistrationModalOpen(true);
         }
      } catch (err) {
         console.error('Error checking user:', err);
         setSnackbarMessage('Failed to process request. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   const handleRegistration = async () => {
      if (!email.trim() || !password.trim() || !name.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         await processInvitationAction(pendingAction || 'accept', email, password, name);
         setRegistrationModalOpen(false);
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const processInvitationAction = async (
      action: 'accept' | 'decline',
      userEmail: string,
      userPassword?: string,
      userName?: string
   ) => {
      if (!invitation) return;

      setProcessing(true);
      setError(null);

      try {
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/network/public-accept`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  invitationId: requestId || invitation.id,
                  email: userEmail,
                  password: userPassword,
                  name: userName,
                  action: action,
                  token: token,
               }),
            }
         );

         const result = await response.json();

         if (result.success) {
            // If user just registered (has password), sign them in
            if (userPassword) {
               try {
                  await signInWithEmailAndPassword(auth, userEmail, userPassword);
                  // Refresh invitation status to ensure invited flag is loaded
                  await refreshInvitationStatus();
               } catch (loginErr) {
                  console.error('Error signing in after registration:', loginErr);
               }
            }

            setSuccess(result.message || `Invitation ${action}ed successfully!`);
            setSnackbarMessage(result.message || `Invitation ${action}ed successfully!`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            setTimeout(() => {
               // Always redirect to dashboard after accepting or declining
               navigate('/dashboard');
            }, 2000);
         } else {
            if (result.requiresRegistration) {
               setEmail(userEmail);
               setRegistrationModalOpen(true);
            } else {
               setSnackbarMessage(result.error || `Failed to ${action} invitation`);
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         }
      } catch (err) {
         console.error(`Error ${action}ing invitation:`, err);
         setSnackbarMessage(`Failed to ${action} invitation. Please try again.`);
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setProcessing(false);
      }
   };

   if (loading) {
      return (
         <Container
            maxWidth='sm'
            sx={{
               mt: isMobile ? 2 : 4,
               px: isMobile ? 1 : 2,
               minHeight: '100vh',
               bgcolor: isMobile ? 'grey.50' : 'background.default',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: isMobile ? '40vh' : '50vh',
               }}
            >
               <CircularProgress size={isMobile ? 40 : 48} />
            </Box>
         </Container>
      );
   }

   if (error && !invitation) {
      return (
         <Container
            maxWidth='sm'
            sx={{
               mt: isMobile ? 2 : 4,
               px: isMobile ? 1 : 2,
               minHeight: '100vh',
               bgcolor: isMobile ? 'grey.50' : 'background.default',
            }}
         >
            <Paper
               sx={{
                  p: isMobile ? 1.5 : 2.5,
                  textAlign: 'center',
                  borderRadius: isMobile ? 3 : 2,
                  boxShadow: isMobile ? 2 : 3,
               }}
            >
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  gutterBottom
                  color='error'
                  sx={{
                     fontSize: isMobile ? '1.2rem' : '1.5rem',
                     fontWeight: 600,
                  }}
               >
                  Invalid Invitation
               </Typography>
               <Typography
                  variant='body1'
                  color='text.secondary'
                  paragraph
                  sx={{
                     fontSize: isMobile ? '0.9rem' : '1rem',
                     mb: isMobile ? 1.5 : 2,
                  }}
               >
                  {error}
               </Typography>
               <Button
                  variant='outlined'
                  onClick={() => navigate('/dashboard')}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     mt: isMobile ? 1 : 1.5,
                     py: isMobile ? 1.5 : 1,
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? '0.9rem' : '0.875rem',
                     borderRadius: isMobile ? 2 : 1,
                  }}
               >
                  Go Home
               </Button>
            </Paper>
         </Container>
      );
   }

   if (success) {
      return (
         <Container
            maxWidth='sm'
            sx={{
               mt: isMobile ? 2 : 4,
               px: isMobile ? 1 : 2,
               minHeight: '100vh',
               bgcolor: isMobile ? 'grey.50' : 'background.default',
            }}
         >
            <Paper
               sx={{
                  p: isMobile ? 1.5 : 2.5,
                  textAlign: 'center',
                  borderRadius: isMobile ? 3 : 2,
                  boxShadow: isMobile ? 2 : 3,
               }}
            >
               <CheckCircleIcon
                  sx={{
                     fontSize: isMobile ? 48 : 64,
                     color: 'success.main',
                     mb: isMobile ? 1.5 : 2,
                  }}
               />
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  gutterBottom
                  color='success.main'
                  sx={{
                     fontSize: isMobile ? '1.2rem' : '1.5rem',
                     fontWeight: 600,
                  }}
               >
                  {success}
               </Typography>
               <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{
                     fontSize: isMobile ? '0.9rem' : '1rem',
                  }}
               >
                  Redirecting you to your network...
               </Typography>
            </Paper>
         </Container>
      );
   }

   if (!invitation) {
      return (
         <Container maxWidth='sm' sx={{ mt: 8 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant='h5' gutterBottom>
                  Loading invitation...
               </Typography>
               <CircularProgress />
            </Paper>
         </Container>
      );
   }

   // Check if invitation has already been processed
   if (invitation.status !== 'pending') {
      const statusMessage =
         invitation.status === 'accepted'
            ? 'This invitation has already been accepted.'
            : invitation.status === 'declined'
              ? 'This invitation has been declined.'
              : 'This invitation is no longer valid.';

      return (
         <Container maxWidth='sm' sx={{ mt: isMobile ? 2 : 4 }}>
            <Paper sx={{ p: isMobile ? 1.5 : 2.5, textAlign: 'center' }}>
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  gutterBottom
                  sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}
               >
                  Invitation Already Processed
               </Typography>
               <Typography
                  variant='body2'
                  color='text.secondary'
                  paragraph
                  sx={{ fontSize: isMobile ? '0.85rem' : '0.9rem' }}
               >
                  {statusMessage}
               </Typography>
               <Button
                  variant='outlined'
                  onClick={() => navigate('/people')}
                  size={isMobile ? 'medium' : 'medium'}
                  sx={{ mt: isMobile ? 1 : 1.5, fontSize: isMobile ? '0.8rem' : '0.85rem' }}
               >
                  View My Network
               </Button>
            </Paper>
         </Container>
      );
   }

   return (
      <Container
         maxWidth='sm'
         sx={{
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2,
            minHeight: '100vh',
            bgcolor: isMobile ? 'grey.50' : 'background.default',
            pb: isMobile ? 4 : 0,
         }}
      >
         <Paper
            sx={{
               overflow: 'hidden',
               borderRadius: isMobile ? 3 : 2,
               boxShadow: isMobile ? 2 : 3,
            }}
         >
            {/* Header */}
            <Box
               sx={{
                  background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                  color: 'white',
                  p: isMobile ? 1.5 : 2.5,
                  textAlign: 'center',
               }}
            >
               <PersonAddIcon
                  sx={{
                     fontSize: isMobile ? 32 : 36,
                     mb: isMobile ? 1 : 1.5,
                  }}
               />
               <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  gutterBottom
                  sx={{
                     fontSize: isMobile ? '1.1rem' : '1.5rem',
                     fontWeight: 700,
                  }}
               >
                  Network Invitation
               </Typography>
               <Typography
                  variant='body2'
                  sx={{
                     opacity: 0.9,
                     fontSize: isMobile ? '0.8rem' : '0.9rem',
                  }}
               >
                  You've been invited to connect
               </Typography>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: isMobile ? 1.5 : 2.5 }}>
               <Box
                  sx={{
                     textAlign: 'center',
                     mb: isMobile ? 2 : 2.5,
                  }}
               >
                  <Avatar
                     src={invitation.fromUserPhoto}
                     sx={{
                        width: isMobile ? 60 : 80,
                        height: isMobile ? 60 : 80,
                        mx: 'auto',
                        mb: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '1.2rem' : '1.5rem',
                        boxShadow: 2,
                     }}
                  >
                     {invitation.fromUserName.charAt(0).toUpperCase()}
                  </Avatar>

                  <Typography
                     variant='h6'
                     gutterBottom
                     sx={{
                        fontSize: isMobile ? '1rem' : '1.2rem',
                        fontWeight: 600,
                     }}
                  >
                     {invitation.fromUserName}
                  </Typography>

                  <Typography
                     variant='body2'
                     color='text.secondary'
                     gutterBottom
                     sx={{
                        fontSize: isMobile ? '0.75rem' : '0.8rem',
                     }}
                  >
                     {invitation.fromUserEmail}
                  </Typography>

                  <Typography
                     variant='body2'
                     sx={{
                        mt: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                     }}
                  >
                     wants to connect with you on wetrippen
                  </Typography>
               </Box>

               {/* Relationship type */}
               {invitation.relationshipType && (
                  <Box
                     sx={{
                        textAlign: 'center',
                        mb: isMobile ? 1.5 : 2,
                     }}
                  >
                     <Chip
                        icon={getRelationshipIcon(invitation.relationshipType)}
                        label={
                           invitation.relationshipLabel ||
                           relationshipLabels[invitation.relationshipType]
                        }
                        color='primary'
                        variant='outlined'
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                           fontSize: isMobile ? '0.8rem' : '0.9rem',
                           height: isMobile ? '28px' : '32px',
                        }}
                     />
                  </Box>
               )}

               {/* Personal message */}
               {invitation.message && (
                  <Card
                     variant='outlined'
                     sx={{
                        mb: isMobile ? 2 : 2.5,
                        borderRadius: isMobile ? 2 : 1,
                     }}
                  >
                     <CardContent sx={{ p: isMobile ? 1.2 : 1.5 }}>
                        <Typography
                           variant='subtitle2'
                           color='text.secondary'
                           gutterBottom
                           sx={{
                              fontSize: isMobile ? '0.75rem' : '0.8rem',
                              fontWeight: 600,
                           }}
                        >
                           Personal Message:
                        </Typography>
                        <Typography
                           variant='body2'
                           sx={{
                              fontStyle: 'italic',
                              fontSize: isMobile ? '0.8rem' : '0.85rem',
                              lineHeight: isMobile ? 1.4 : 1.5,
                           }}
                        >
                           "{invitation.message}"
                        </Typography>
                     </CardContent>
                  </Card>
               )}

               <Divider sx={{ my: isMobile ? 1.5 : 2 }} />

               {/* Actions */}
               <Box sx={{ textAlign: 'center' }}>
                  <Typography
                     variant='body2'
                     color='text.secondary'
                     sx={{
                        mb: isMobile ? 1.5 : 2,
                        fontSize: isMobile ? '0.8rem' : '0.85rem',
                        lineHeight: isMobile ? 1.4 : 1.5,
                     }}
                  >
                     Would you like to accept this invitation and add {invitation.fromUserName} to
                     your network?
                  </Typography>

                  <Box
                     sx={{
                        display: 'flex',
                        gap: isMobile ? 1 : 1.5,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        flexDirection: isMobile ? 'column' : 'row',
                     }}
                  >
                     <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<CancelIcon sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }} />}
                        onClick={handleDecline}
                        disabled={processing}
                        size={isMobile ? 'medium' : 'medium'}
                        sx={{
                           fontSize: isMobile ? '0.8rem' : '0.85rem',
                           py: isMobile ? 1 : 0.8,
                           px: isMobile ? 2.5 : 2,
                           borderRadius: isMobile ? 2 : 1,
                           minWidth: isMobile ? '100%' : 120,
                        }}
                     >
                        Decline
                     </Button>

                     <Button
                        variant='contained'
                        color='primary'
                        startIcon={
                           processing ? (
                              <CircularProgress size={isMobile ? 12 : 14} color='inherit' />
                           ) : (
                              <CheckCircleIcon sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }} />
                           )
                        }
                        onClick={handleAccept}
                        disabled={processing}
                        size={isMobile ? 'medium' : 'medium'}
                        sx={{
                           minWidth: isMobile ? '100%' : 120,
                           fontSize: isMobile ? '0.8rem' : '0.85rem',
                           py: isMobile ? 1 : 0.8,
                           px: isMobile ? 2.5 : 2,
                           borderRadius: isMobile ? 2 : 1,
                        }}
                     >
                        {processing ? 'Accepting...' : 'Accept'}
                     </Button>
                  </Box>
               </Box>

               {error && (
                  <Alert severity='error' sx={{ mt: 3 }}>
                     {error}
                  </Alert>
               )}
            </CardContent>
         </Paper>

         {/* Registration Modal */}
         <Dialog
            open={registrationModalOpen}
            onClose={() => setRegistrationModalOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     {pendingAction === 'accept' ? 'Accept' : 'Decline'} Invitation
                  </Typography>
                  <IconButton onClick={() => setRegistrationModalOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to {pendingAction === 'accept' ? 'accept' : 'decline'} this
                  invitation.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
                  disabled
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setRegistrationModalOpen(false)}>Cancel</Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Submit'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Container>
   );
};

export default AcceptNetworkInvite;
